import React, { useEffect, useRef, useState, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import Question from "../../components/Question";
import MoveButton from "../../components/MoveButton";
import { Stack, Box, useTheme, useMediaQuery } from "@mui/material";
import { setCaseId, setPatientID, setRetIntakeId, setReturningCondition as updateCondition } from '../../redux/userSlice';
import { formatDataNoPhotosReturning } from '../../lib/formatting';
import { callBackendApiPost } from '../../lib/api';
import CustomSpinner from "../../components/CustomSpinner";
import { setCardFront, setCardBack } from "../../redux/insuranceSlice";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";
import { MinorUserContext } from "../../context/UserNameProvider";
import logDrtEvent from '../../lib/logging';

export default function FeelingNow() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const user = useSelector((state) => state.user);
    const insurance = useSelector((state) => state.insurance);
    const stripe = useSelector((state) => state.stripe);
    const { portal = false } = location.state || {};
    const patient = useSelector((state) => state.user.selectedReturningPatient);
    const { isMinor } = useContext(MinorUserContext);

    const hasCalledUpdateRef = useRef(false);

    const isUserReady = !!(user && Object.keys(user).length);
    const isInsuranceReady = !!(insurance && Object.keys(insurance).length);
    const isStripeReady = !!(stripe && Object.keys(stripe).length);

    const [loading, setLoading] = useState(true);

    const options = {
        "Better": "better",
        "Worse": "worse",
        "Same": "same",
    };

    const assessedCases = useMemo(
        () =>
            (patient?.cases ?? []).filter((c) => {
                const a = c?.assessment;
                if (a == null) return false;
                if (Array.isArray(a)) return a.length > 0;
                if (typeof a === 'object') return Object.keys(a).length > 0;
                return !!a;
            }),
        [patient?.cases]
    );

    const handleClick = (option) => {
        dispatch(updateCondition(options[option]));
        setPageAndDeferNavigate(dispatch, '/picked-medication', () =>
                navigate('/picked-medication', { state: { portal } })
            );
    };

    const handleBack = () => {
        if (assessedCases.length > 1) {
            const state = { portal, patient, disableAutoSelect: true };

            if (portal) {
                navigate('/patient-cases', { state });
            } else {
                setPageAndDeferNavigate(dispatch, '/patient-cases', () =>
                    navigate('/patient-cases', { state })
                );
            }
            return;
        }

        if (portal) {
            navigate('/returning-reason', { state: { portal } });
        } else if (user.returning && isMinor) {
            setPageAndDeferNavigate(dispatch, '/minor/instructions', () =>
                navigate('/minor/instructions', { state: { returning: true } })
            );
        } else {
            setPageAndDeferNavigate(dispatch, '/returning-reason', () =>
                navigate('/returning-reason', { state: { portal } })
            );
        }
    };

    useEffect(() => {
        if (hasCalledUpdateRef.current) return;
        if (!isUserReady || !isInsuranceReady || !isStripeReady) return;

        hasCalledUpdateRef.current = true;
        setLoading(true);

        const data = formatDataNoPhotosReturning(user, insurance, stripe);

        callBackendApiPost('/api/v1/ret-update', data)
            .then(response => response.json())
            .then(returnedData => {
                setLoading(false);
                if (returnedData?.code === 404 && returnedData?.description === "Patient not found in tellescope.") {
                    logDrtEvent(`Error while fetching and updating returning patient data, Patient not found in tellescope :${returnedData?.code}`);
                    navigate("/error", { state: { returning: true } });
                } else if (returnedData?.case_id) {
                    dispatch(setPatientID(returnedData?.patient?.id));
                    dispatch(setCaseId(returnedData.case_id));
                    dispatch(setRetIntakeId(returnedData.return_intake_id));
                    if (returnedData?.patient?.insurance?.cardFront) {
                        dispatch(setCardFront(returnedData?.patient?.insurance?.cardFront));
                    }
                    if (returnedData?.patient?.insurance?.cardBack) {
                        dispatch(setCardBack(returnedData?.patient?.insurance?.cardBack));
                    }
                }
            })
            .catch((error) => {
                setLoading(false);
                logDrtEvent(`Error while fetching and updating returning patient data :${error}`);
                navigate("/error", { state: { returning: true } });
            });
    }, [isUserReady, isInsuranceReady, isStripeReady, dispatch]);

    if (loading) {
        return <CustomSpinner />;
    }

    return (
        <Question
            returning={true}
            title="Are you feeling better, worse, or the same as before?"
            subtitle="This information will help the dermatologist renew or adjust your prescription or follow up on your condition."
            noBack={false}
            onBack={handleBack}
            input={
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    width="100%"
                >
                    <Stack
                        spacing={2}
                        width="100%"
                        maxWidth="sm"
                        alignItems="center"
                    >
                        {Object.keys(options).map((option) => (
                            <MoveButton
                                key={option}
                                onClick={() => handleClick(option)}
                                title={option}
                                selected={user.returningCondition === options[option]}
                                fullWidth
                                sx={{
                                    width: isDesktop ? '100%' : '100%',
                                    maxWidth: '400px',
                                }}
                            />
                        ))}
                    </Stack>
                </Box>
            }
        />
    );
}
